import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Habit, HabitCompletion, JournalEntry } from './challenge.model';

@Component({
    selector: 'app-history-modal',
    templateUrl: './history-modal.component.html',
    styleUrls: ['./history-modal.component.scss'],
    standalone: false
})
export class HistoryModalComponent implements OnInit {
  @Input() habits: Habit[] = [];
  @Input() completions: HabitCompletion[] = [];
  @Input() journals: { [date: string]: JournalEntry } = {};
  @Input() today: string = '';

  @Output() toggleCompletion = new EventEmitter<{ habit: Habit, date: string }>();
  @Output() journalChange = new EventEmitter<{ date: string, patch: Partial<Omit<JournalEntry, 'date'>> }>();
  @Output() close = new EventEmitter<void>();

  viewYear = 0;
  viewMonth = 0; // 0-based
  selectedDate: string | null = null;

  ngOnInit(): void {
    const [y, m] = (this.today || this.todayLocal()).split('-').map(Number);
    this.viewYear = y;
    this.viewMonth = m - 1;
  }

  private todayLocal(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  get monthLabel(): string {
    return new Date(this.viewYear, this.viewMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  prevMonth() {
    if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; }
    else this.viewMonth--;
  }
  nextMonth() {
    if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; }
    else this.viewMonth++;
  }

  get monthCells(): { date: string, day: number, inMonth: boolean, isToday: boolean, status: 'none' | 'partial' | 'full' }[] {
    const first = new Date(this.viewYear, this.viewMonth, 1);
    const last = new Date(this.viewYear, this.viewMonth + 1, 0);
    const startDay = first.getDay();
    const daysInMonth = last.getDate();
    const cells: { date: string, day: number, inMonth: boolean, isToday: boolean, status: 'none' | 'partial' | 'full' }[] = [];

    // Previous month padding
    if (startDay > 0) {
      const prev = new Date(this.viewYear, this.viewMonth, 0);
      const prevDays = prev.getDate();
      for (let i = startDay - 1; i >= 0; i--) {
        const d = new Date(this.viewYear, this.viewMonth - 1, prevDays - i);
        cells.push(this.makeCell(d, false));
      }
    }
    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(this.viewYear, this.viewMonth, i);
      cells.push(this.makeCell(d, true));
    }
    // Pad to multiple of 7
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1];
      const [y, m, day] = last.date.split('-').map(Number);
      const next = new Date(y, m - 1, day + 1);
      cells.push(this.makeCell(next, false));
    }
    return cells;
  }

  private makeCell(d: Date, inMonth: boolean) {
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const status = this.statusForDate(date);
    return { date, day: d.getDate(), inMonth, isToday: date === this.today, status };
  }

  private statusForDate(date: string): 'none' | 'partial' | 'full' {
    // Habits that existed on this date and weren't archived by this date, daily only
    const ts = Date.parse(date + 'T00:00:00');
    const eligible = this.habits.filter(h =>
      h.frequency === 'daily' &&
      h.createdAt <= ts &&
      (!h.archivedAt || h.archivedAt > ts)
    );
    if (!eligible.length) return 'none';
    const completedIds = new Set(
      this.completions.filter(c => c.date === date).map(c => c.habitId)
    );
    const completedCount = eligible.filter(h => completedIds.has(h.id)).length;
    if (completedCount === 0) return 'none';
    if (completedCount === eligible.length) return 'full';
    return 'partial';
  }

  selectDate(date: string) { this.selectedDate = date; }
  closeInspector() { this.selectedDate = null; }

  get habitsForSelectedDate(): Habit[] {
    if (!this.selectedDate) return [];
    const ts = Date.parse(this.selectedDate + 'T00:00:00');
    return this.habits.filter(h =>
      h.createdAt <= ts && (!h.archivedAt || h.archivedAt > ts)
    );
  }

  completionFor(habitId: string, date: string): HabitCompletion | null {
    return this.completions.find(c => c.habitId === habitId && c.date === date) ?? null;
  }

  journalFor(date: string): JournalEntry | null { return this.journals[date] ?? null; }

  toggleDay(habit: Habit) {
    if (!this.selectedDate) return;
    this.toggleCompletion.emit({ habit, date: this.selectedDate });
  }

  onInspectorJournalText(text: string) {
    if (!this.selectedDate) return;
    this.journalChange.emit({ date: this.selectedDate, patch: { text } });
  }

  onInspectorGeneral(value: number) {
    if (!this.selectedDate) return;
    this.journalChange.emit({ date: this.selectedDate, patch: { generalRating: value as any } });
  }

  onInspectorHappiness(value: number) {
    if (!this.selectedDate) return;
    this.journalChange.emit({ date: this.selectedDate, patch: { happinessRating: value as any } });
  }
}
